import React from 'react';
import { dayjs } from '../support/helpers/dayjs.ts';
import { IonCard, IonCardContent, IonIcon, IonItem, IonText } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { Page } from '../components/page/Page.tsx';
import { AttachmentList } from '../notes/AttachmentList.tsx';
import { IAttachment } from '../notes/NotesClient.ts';
import { formatLogDateAndTime, initCap } from '../support/helpers/format.ts';
import { AlertTriangleIcon, ClipboardIcon, IIconComponentWithRaw, PlaneIcon, WrenchIcon } from '../components/icons/Icons.tsx';
import { AircraftLogState } from './AircraftLogState.tsx';
import { HistoryViewModel } from '../app/HistoryViewModel.ts';
import { Button } from '../components/button/Button.tsx';
import { Spinner } from '../components/spinner/Spinner.tsx';
import { Refresher } from '../components/refresher/Refresher.tsx';
import { usePageView } from '../support/tracking/usePageView.tsx';
import { IAircraftNoteType } from './IAircraftNote.ts';
import { Badge } from '../components/badge/Badge.tsx';

const TypeIcon: React.FC<{type: IAircraftNoteType | 'mission'}> = ({ type }) => {
    let Icon: IIconComponentWithRaw;
    let padding = '5px 5px';
    if (type === 'mission') {
        Icon = PlaneIcon;
        // a triangle doesn't look centered in a circle
        padding = '5px 4px';
    } else if (type === 'maintenance') {
        Icon = WrenchIcon;
    } else if (type === 'incident') {
        Icon = AlertTriangleIcon;
        // a triangle doesn't look centered in a circle
        padding = '4px 5px';
    } else {
        Icon = ClipboardIcon;
    }
    return (
        <div style={{width: 32, height: 32, padding, border: '1.5px solid var(--ion-color-medium)', borderRadius: '50%'}}>
            <IonIcon icon={Icon.raw} color="medium" style={{fontSize: 18}} />
        </div>
    );
};

interface ILogEntry {
    id: string;
    title: string;
    type: IAircraftNoteType | 'mission';
    dateTime: string;
    attachments: IAttachment[];
}

export const AircraftLogPage: React.FC<{aircraftId: string}> = ({ aircraftId }) => {
    usePageView('Drone Log');

    const history = useInstance(HistoryViewModel);

    const logState = useInstance(AircraftLogState);
    useSubscription(() => logState);

    const entries = logState.getEntries(aircraftId);

    const addNote = () => {
        history.push(`/fleet/${aircraftId}/log/add`);
    };

    const viewEntry = (entry: ILogEntry) => {
        if (entry.type === 'mission') {
            history.push(`/missions/${entry.id}`);
        } else {
            history.push(`/fleet/${aircraftId}/log/${entry.id}`);
        }
    };

    const isFuture = (entry: ILogEntry) => {
        return dayjs().isBefore(entry.dateTime);
    };

    return (
        <Page title="Drone Log" onBack={() => history.back(`/fleet/${aircraftId}`)}>

            <Refresher onRefresh={() => logState.reload(aircraftId)} />

            {!entries && (
                <Spinner centered />
            )}

            {entries && (
                <>
                    {entries.length === 0 ? (
                        <IonCard><IonCardContent>
                            <h2 style={{marginBottom: 30}}>No entries yet, start tracking Maintenance, Incidents, and more!</h2>
                            <div style={{margin: 20}}>
                                <Button expand="block" onClick={addNote}>New Entry</Button>
                            </div>
                        </IonCardContent></IonCard>
                    ) : (
                        <div style={{margin: 20}}>
                            <Button expand="block" onClick={addNote}>New Entry</Button>
                        </div>
                    )}

                    <div style={{ width: '100%' }}>
                        {entries.map(entry => (
                            <IonItem
                                key={`${entry.type}-${entry.id}`}
                                button
                                detail
                                data-type="note-item"
                                onClick={() => viewEntry(entry)}
                            >
                                <div style={{display: 'flex', flexDirection: 'column', gap: 15, width: '100%', padding: '15px 5px 15px 0'}}>

                                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 15}}>

                                        <TypeIcon type={entry.type as any} />

                                        <div style={{display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0, overflow: 'hidden'}}>
                                            <IonText style={{fontSize: 18, fontWeight: 'bold'}}>{initCap(entry.type)}</IonText>
                                            <IonText style={{fontSize: 14, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} color="medium">{entry.title}</IonText>
                                        </div>

                                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5}}>
                                            {isFuture(entry) &&
                                                <Badge size="small" color="primary">Upcoming</Badge>
                                            }
                                            <IonText style={{fontSize: 14}}>{formatLogDateAndTime(entry.dateTime)}</IonText>
                                        </div>

                                    </div>

                                    <AttachmentList attachments={entry.attachments} />

                                </div>
                            </IonItem>
                        ))}
                    </div>
                </>
            )}

        </Page>
    );
};
